import { useLayoutEffect, useRef } from "react";

/**
 * Custom hook to scroll to a specific message in a container
 *
 * @param {Object} options - Configuration options
 * @param {string|number} options.messageId - The ID of the message to scroll to
 * @param {React.RefObject} options.containerRef - Reference to the scrollable container (optional)
 * @param {Array} options.messages - Array of messages to watch for changes
 * @param {string} options.dataAttribute - Custom data attribute name (default: 'data-message-id')
 * @param {number} options.delay - Delay in ms before scrolling (default: 300)
 * @param {string} options.behavior - Scroll behavior: 'smooth' or 'auto' (default: 'smooth')
 * @param {string} options.block - Scroll alignment: 'start', 'center', 'end', 'nearest' (default: 'center')
 * @param {Function} options.onScrollComplete - Callback when scroll is complete
 * @param {Function} options.onMessageNotFound - Callback when message is not found
 *
 * @example
 * // Basic usage
 * useScrollToMessage({
 *   messageId: '12345',
 *   messages: selectedLead?.messages,
 * });
 *
 * @example
 * // Advanced usage with callbacks
 * useScrollToMessage({
 *   messageId: messageIdFromUrl,
 *   containerRef: msgContainerRef,
 *   messages: selectedLead?.messages,
 *   behavior: 'smooth',
 *   block: 'center',
 *   delay: 500,
 *   onScrollComplete: () => console.log('Scrolled to message'),
 *   onMessageNotFound: () => console.log('Message not found'),
 * });
 */
const useScrollToMessage = ({
  messageId,
  containerRef,
  messages = [],
  dataAttribute = "data-message-id",
  delay = 300,
  behavior = "smooth",
  block = "center",
  onScrollComplete,
  onMessageNotFound,
} = {}) => {
  // Keep the callbacks in refs so their (usually inline, per-render) identity
  // does NOT retrigger the scroll effect. The activity center re-renders
  // frequently (sockets, typing, etc.); without this the effect below would
  // cancel + restart its poll on every render and never survive long enough
  // to find and scroll to the target message.
  const onScrollCompleteRef = useRef(onScrollComplete);
  const onMessageNotFoundRef = useRef(onMessageNotFound);
  onScrollCompleteRef.current = onScrollComplete;
  onMessageNotFoundRef.current = onMessageNotFound;

  // Re-run the effect when the message set meaningfully changes (target may
  // appear in a later fetch batch), keyed by a stable primitive rather than
  // the array identity so unrelated re-renders don't restart the poll.
  const messagesKey = Array.isArray(messages) ? messages.length : 0;

  useLayoutEffect(() => {
    // Skip if no messageId is provided
    if (!messageId) {
      return;
    }

    // If containerRef is provided, ensure it's mounted
    if (containerRef && !containerRef.current) {
      return;
    }

    // A single timed attempt is unreliable: depending on network/render
    // timing the target element may not exist yet at ``delay`` ms (giving
    // up too early) or a competing scroll may run afterwards. Instead poll
    // for the element and scroll as soon as it appears, retrying up to a
    // bounded number of times so it also survives a wipe+refetch (e.g. the
    // auto tab-switch on a deep link).
    let cancelled = false;
    let attemptTimeoutId;
    const correctionTimeoutIds = [];
    let imgCleanup = null;
    const RETRY_INTERVAL = 60; // ms between polls
    const MAX_ATTEMPTS = 50; // ~3s total once polling starts

    // After the first scroll, late-loading media ABOVE the target (images,
    // video thumbnails) can grow the thread and push the target out of view
    // (e.g. behind the compose box). Re-scroll a few times as layout settles,
    // and also whenever an image in the scroll container finishes loading.
    const keepInView = el => {
      const rescroll = () => {
        if (cancelled || !document.body.contains(el)) return;
        el.scrollIntoView({ behavior, block });
      };
      [150, 400, 900, 1500].forEach(ms => {
        correctionTimeoutIds.push(setTimeout(rescroll, ms));
      });
      // Watch images in the surrounding scroll container so a slow-loading
      // media item that resolves after the last timer still re-centers.
      const scrollContainer =
        el.closest(".antdList") || el.closest("[data-message-scroll]") || el.parentElement;
      if (scrollContainer) {
        const imgs = Array.from(scrollContainer.querySelectorAll("img")).filter(i => !i.complete);
        const onImg = () => rescroll();
        imgs.forEach(i => {
          i.addEventListener("load", onImg);
          i.addEventListener("error", onImg);
        });
        imgCleanup = () => {
          imgs.forEach(i => {
            i.removeEventListener("load", onImg);
            i.removeEventListener("error", onImg);
          });
        };
      }
    };

    const tryScroll = (attempt = 0) => {
      if (cancelled) return;
      const targetElement = document.querySelector(`[${dataAttribute}="${messageId}"]`);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior, block });
        keepInView(targetElement);
        if (onScrollCompleteRef.current) {
          onScrollCompleteRef.current(targetElement);
        }
        return;
      }

      if (attempt + 1 >= MAX_ATTEMPTS) {
        if (onMessageNotFoundRef.current) {
          onMessageNotFoundRef.current(messageId);
        }
        return;
      }

      attemptTimeoutId = setTimeout(() => tryScroll(attempt + 1), RETRY_INTERVAL);
    };

    // useLayoutEffect runs after DOM mutations but before paint; keep the
    // initial delay before the first poll so the first render settles.
    const timeoutId = setTimeout(() => tryScroll(0), delay);

    // Cleanup on unmount / dependency change
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      clearTimeout(attemptTimeoutId);
      correctionTimeoutIds.forEach(clearTimeout);
      if (imgCleanup) imgCleanup();
    };
    // Callbacks intentionally excluded (read via refs) so frequent re-renders
    // don't restart the poll. Keyed by messagesKey (length) instead of the
    // array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, messagesKey, dataAttribute, delay, behavior, block]);
};

export default useScrollToMessage;
